/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AppWindow,
  Boxes,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  FileText,
  FolderTree,
  LayoutGrid,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CadrageReadWithTeam,
  Domain,
  ForgeApplicationWithContext,
  Organization,
  UseCaseSummary,
} from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Common/Card';
import domainService from '@/services/domainService';
import useCaseService from '@/services/useCaseService';
import cadrageService from '@/services/cadrageService';
import forgeService from '@/services/forgeService';
import { useForgeApiEnabled } from '@/stores/forgeApiStore';

interface OrganizationStructureTreeProps {
  organization: Organization;
}

interface UseCaseNode extends UseCaseSummary {
  cadrages: CadrageReadWithTeam[];
  app: ForgeApplicationWithContext | null;
}

interface DomainNode extends Domain {
  useCases: UseCaseNode[];
}

const TreeRow: React.FC<{
  expanded: boolean;
  onToggle?: () => void;
  icon: React.ReactNode;
  label: React.ReactNode;
  meta?: React.ReactNode;
  hasChildren: boolean;
  depth: number;
}> = ({ expanded, onToggle, icon, label, meta, hasChildren, depth }) => {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-center gap-2 rounded-md py-1.5 pr-2 hover:bg-muted/50"
      style={{ paddingLeft: depth * 16 + 4 }}
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={!hasChildren}
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground disabled:opacity-40"
        aria-label={expanded ? t('organizationStructure.collapse') : t('organizationStructure.expand')}
      >
        {hasChildren ? (
          expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )
        ) : (
          <span className="h-4 w-4" />
        )}
      </button>
      <span className="flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground">
        {icon}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm">{label}</span>
      {meta && <span className="shrink-0 text-xs text-muted-foreground">{meta}</span>}
    </div>
  );
};

export const OrganizationStructureTree: React.FC<OrganizationStructureTreeProps> = ({
  organization,
}) => {
  const { t } = useTranslation();
  const forgeApiEnabled = useForgeApiEnabled() === true;
  const [domains, setDomains] = useState<DomainNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const rawDomains = await domainService.getOrganizationDomains(organization.id);

        const apps = forgeApiEnabled && organization.forge_client_id
          ? (await forgeService
              .listApplicationsWithContext()
              .catch(() => [] as ForgeApplicationWithContext[])).filter(
              (a) => a.client_id === organization.forge_client_id
            )
          : ([] as ForgeApplicationWithContext[]);

        const useCaseLists = await Promise.all(
          rawDomains.map((d) =>
            useCaseService.list({ domain_id: d.id }).catch(() => [] as UseCaseSummary[])
          )
        );

        const allUseCases = useCaseLists.flat();
        const cadragesByUseCase = await Promise.all(
          allUseCases.map((uc) =>
            cadrageService
              .getCadragesByUseCase(uc.id)
              .catch(() => [] as CadrageReadWithTeam[])
          )
        );

        const cadrageMap = new Map<string, CadrageReadWithTeam[]>();
        allUseCases.forEach((uc, idx) => {
          cadrageMap.set(uc.id, cadragesByUseCase[idx]);
        });

        const appsByUseCase = new Map<string, ForgeApplicationWithContext>();
        apps.forEach((app) => {
          if (app.use_case_id) appsByUseCase.set(app.use_case_id, app);
        });

        const tree: DomainNode[] = rawDomains.map((d, idx) => ({
          ...d,
          useCases: useCaseLists[idx].map((uc) => ({
            ...uc,
            cadrages: cadrageMap.get(uc.id) ?? [],
            app:
              appsByUseCase.get(uc.id) ??
              (uc.forge_application_id
                ? apps.find((a) => a.app_id === uc.forge_application_id) ?? null
                : null),
          })),
        }));

        if (cancelled) return;
        setDomains(tree);
        const initialExpanded: Record<string, boolean> = {};
        tree.forEach((d) => {
          initialExpanded[`d:${d.id}`] = true;
          d.useCases.forEach((uc) => {
            initialExpanded[`u:${uc.id}`] = true;
          });
        });
        setExpanded(initialExpanded);
      } catch (err: any) {
        if (!cancelled) {
          console.error('Error loading organization structure:', err);
          setError(err?.message || t('global.error', 'Erreur'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [organization.id, organization.forge_client_id, forgeApiEnabled, t]);

  const toggle = (key: string) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const totalCounts = useMemo(() => {
    let useCases = 0;
    let cadrages = 0;
    let apps = 0;
    domains.forEach((d) => {
      useCases += d.useCases.length;
      d.useCases.forEach((uc) => {
        cadrages += uc.cadrages.length;
        if (uc.app) apps += 1;
      });
    });
    return { useCases, cadrages, apps };
  }, [domains]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FolderTree className="h-5 w-5" />
          {t('organizationStructure.title', "Structure de l'organisation")}
        </CardTitle>
        {!loading && !error && (
          <p className="text-xs text-muted-foreground">
            {t('organizationStructure.summary',
              '{{domains}} domaines · {{useCases}} cas d\'usage · {{cadrages}} cadrages · {{apps}} apps',
              {
                domains: domains.length,
                useCases: totalCounts.useCases,
                cadrages: totalCounts.cadrages,
                apps: totalCounts.apps,
              })}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary" />
          </div>
        ) : error ? (
          <div className="text-sm text-destructive">{error}</div>
        ) : domains.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            {t('organizationStructure.empty', 'Aucun domaine défini pour cette organisation.')}
          </div>
        ) : (
          <div className="space-y-1">
            {domains.map((d) => {
              const dKey = `d:${d.id}`;
              const dExpanded = !!expanded[dKey];
              return (
                <div key={d.id}>
                  <TreeRow
                    depth={0}
                    expanded={dExpanded}
                    onToggle={() => toggle(dKey)}
                    icon={<LayoutGrid className="h-4 w-4" />}
                    label={<span className="font-medium">{d.name}</span>}
                    meta={t('organizationStructure.useCasesCount', '{{count}} cas d\'usage', {
                      count: d.useCases.length,
                    })}
                    hasChildren={d.useCases.length > 0}
                  />
                  {dExpanded &&
                    d.useCases.map((uc) => {
                      const uKey = `u:${uc.id}`;
                      const uExpanded = !!expanded[uKey];
                      const childCount = uc.cadrages.length + (uc.app ? 1 : 0);
                      return (
                        <div key={uc.id}>
                          <TreeRow
                            depth={1}
                            expanded={uExpanded}
                            onToggle={() => toggle(uKey)}
                            icon={<ClipboardList className="h-4 w-4" />}
                            label={
                              <span className="flex items-center gap-2">
                                <span className="truncate">{uc.name}</span>
                                {uc.status && (
                                  <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                                    {uc.status}
                                  </span>
                                )}
                              </span>
                            }
                            meta={
                              childCount === 0
                                ? undefined
                                : t('organizationStructure.itemsCount', '{{count}} éléments', {
                                    count: childCount,
                                  })
                            }
                            hasChildren={childCount > 0}
                          />
                          {uExpanded && uc.cadrages.map((c) => (
                            <TreeRow
                              key={c.id}
                              depth={2}
                              expanded={false}
                              icon={<FileText className="h-4 w-4" />}
                              label={
                                <span className="flex items-center gap-2">
                                  <span className="truncate">{c.name}</span>
                                  {c.status && (
                                    <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                                      {c.status}
                                    </span>
                                  )}
                                  {c.version != null && (
                                    <span className="text-xs text-muted-foreground">
                                      v{c.version}
                                    </span>
                                  )}
                                </span>
                              }
                              hasChildren={false}
                            />
                          ))}
                          {uExpanded && uc.app && (
                            <TreeRow
                              depth={2}
                              expanded={false}
                              icon={<AppWindow className="h-4 w-4" />}
                              label={
                                <span className="flex items-center gap-2">
                                  <span className="truncate">{uc.app.name}</span>
                                  <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                                    {uc.app.status}
                                  </span>
                                </span>
                              }
                              meta={uc.app.app_type}
                              hasChildren={false}
                            />
                          )}
                          {uExpanded && childCount === 0 && (
                            <div
                              className="text-xs italic text-muted-foreground"
                              style={{ paddingLeft: 2 * 16 + 32 }}
                            >
                              {t('organizationStructure.noChildren', 'Aucun cadrage ni application liée')}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  {dExpanded && d.useCases.length === 0 && (
                    <div
                      className="text-xs italic text-muted-foreground"
                      style={{ paddingLeft: 1 * 16 + 32 }}
                    >
                      {t('organizationStructure.noUseCases', 'Aucun cas d\'usage')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-4 flex items-center gap-3 border-t border-border pt-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <LayoutGrid className="h-3.5 w-3.5" /> {t('organizationStructure.legend.domain', 'Domaine')}
          </span>
          <span className="inline-flex items-center gap-1">
            <ClipboardList className="h-3.5 w-3.5" /> {t('organizationStructure.legend.useCase', "Cas d'usage")}
          </span>
          <span className="inline-flex items-center gap-1">
            <FileText className="h-3.5 w-3.5" /> {t('organizationStructure.legend.cadrage', 'Cadrage')}
          </span>
          {forgeApiEnabled && (
            <span className="inline-flex items-center gap-1">
              <AppWindow className="h-3.5 w-3.5" /> {t('organizationStructure.legend.app', 'Application')}
            </span>
          )}
          <span className="ml-auto inline-flex items-center gap-1">
            <Boxes className="h-3.5 w-3.5" />
            {t('organizationStructure.totalItems', 'total {{count}}', {
              count:
                domains.length +
                totalCounts.useCases +
                totalCounts.cadrages +
                totalCounts.apps,
            })}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default OrganizationStructureTree;
